import { Delete } from "@mui/icons-material";
import {
    Box,
    Button,
    IconButton,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { CustomTextInput } from "components/atoms";
import { Add } from "iconsax-react";
import React, { forwardRef, useRef, useState } from "react";
import {
    DragDropContext,
    Droppable,
    Draggable,
} from "react-beautiful-dnd";
import { generateUUID, showToast, toCamelCase } from "services/utils";
import DialogBox from "./DialogBox";
import { getRefValueByPath, setRefValueByPath } from "../services";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const StructureCell = (props) => {
  const { column, onChange, config = {} } = props;

  return (
    <CustomTextInput
      value={column?.name ?? ""}
      onChange={(e) => onChange(e.target.value ?? "N/A")}
      disabled={config?.isViewMode || column?.isReadOnly}
    />
  );
};

const StructureTable = (props) => {
    const { config = {}, columns: initialColumns = [] } = props
    const [_, forceUpdate] = useState({});
    const render = () => forceUpdate({});

    const stateRef = useRef({
        modelOpen: false,
        columns: [...initialColumns],
        trackChanges: []
    });

    !!config && (config.trackChanges = getRefValueByPath(stateRef, "trackChanges"));

    const { linkingData, onSubmit = false, wrapperSupport = "off", wrapperConfig = {} } = config;
    const { handler = () => { }, isVisible: isSubmitVisible = true, isActive: isSubmitActive = true } = onSubmit ?? {}
    const { syncing = {} } = wrapperConfig
    const isDynamicColumnType = config.isDynamic;

    const checkUniqueNamePresent = (uniqueName) =>
        stateRef.current.columns.some((col) => col?.uniqueName === uniqueName);

    const generateUniqueNameWithPrefix = (baseName) => {
        let i = 0;
        let candidate = baseName;
        while (checkUniqueNamePresent(candidate)) {
            candidate = `${baseName}${++i}`;
        }
        return candidate;
    };

  const getGroupedHeaders = (columns) => {
    const levels = [];
    let maxDepth = 0;

    for (const col of columns) {
      const path = col.groupPath || [];
      maxDepth = Math.max(maxDepth, path.length);

      path.forEach((label, levelIndex) => {
        if (!levels[levelIndex]) levels[levelIndex] = new Map();

        const groupKey = path.slice(0, levelIndex + 1).join(">");
        if (!levels[levelIndex].has(groupKey)) {
          levels[levelIndex].set(groupKey, {
            label,
            colSpan: 0,
            rowSpan: 1,
          });
        }
      });

      const levelIndex = path.length;
      if (!levels[levelIndex]) levels[levelIndex] = new Map();

      const colKey = col.uniqueName;
      levels[levelIndex].set(colKey, {
        label: col.name,
        colSpan: 1,
        rowSpan: 1,
      });

      for (let i = path.length - 1; i >= 0; i--) {
        const groupKey = path.slice(0, i + 1).join(">");
        const group = levels[i].get(groupKey);
        if (group) group.colSpan += 1;
      }
    }

    columns.forEach((col) => {
      if (!col.groupPath || col.groupPath.length === 0) {
        if (!levels[0]) levels[0] = new Map();
        const key = col.uniqueName;
        levels[0].set(key, {
          label: col.name,
          colSpan: 1,
          rowSpan: maxDepth + 1,
        });
      }
    });

    return levels.map((level) => Array.from(level.values()));
  };

  const previewHeaders =
    isDynamicColumnType && getGroupedHeaders(stateRef.current.columns);

    const handleChangeColumnName = (column, index, newValue) => {
        const updated = [...stateRef.current.columns];

        const updatedColumn = { ...updated[index], name: newValue };

        if (updatedColumn?.isNew) {
            let newUniqueName = toCamelCase(newValue);

            if (checkUniqueNamePresent(newUniqueName)) {
                newUniqueName = generateUniqueNameWithPrefix(newUniqueName);
            }

            updatedColumn.uniqueName = newUniqueName;
        }

        updated[index] = updatedColumn;

        const currentTrackChanges = [...(getRefValueByPath(stateRef, "trackChanges") ?? [])];

        const existingIndex = currentTrackChanges.findIndex((col) =>
            col?.uuid ? col?.uuid === updatedColumn?.uuid : col?.uniqueName === updatedColumn?.uniqueName
        );

        if (existingIndex > -1) {
            currentTrackChanges[existingIndex] = updatedColumn;
        } else {
            currentTrackChanges.push(updatedColumn);
        }

        setRefValueByPath(stateRef, "trackChanges", currentTrackChanges);

        stateRef.current.columns = updated;
        render();
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const reordered = Array.from(stateRef.current.columns);
        const [removed] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, removed);

        stateRef.current.columns = reordered;
        render();
    };

    const handleDelete = (index) => {
        stateRef.current.columns = stateRef.current.columns.filter(
            (_, i) => i !== index
        );
        render();
    };

    const handleAdd = () => {
        const dummyColumn = {
            name: "",
            uniqueName: "",
            isNew: true,
            type: "text",
            uuid: generateUUID(),
            // linkedKey: " edd "
        };
        stateRef.current.columns.push(dummyColumn);
        render();
    };

    const finalSubmmit = () => {
        const finalColumns = getRefValueByPath(stateRef, "columns").map((col) => {
            const { uuid = "", isNew = false, ...other } = col
            return other
        })
        if (!((wrapperSupport === "on") && syncing?.submits)) {
            if (!!onSubmit) {
                !!handler && (typeof handler === 'function') && handler(finalColumns)
            }
            getRefValueByPath(stateRef, "modelOpen") ? setRefValueByPath(stateRef, "modelOpen", false) : null
        }

        render()
    }

    const handleSave = () => {
        const { columns } = stateRef.current;

        const hasEmptyField = columns.filter(
            (col) => !col?.name || !col?.uniqueName
        );
        if (hasEmptyField.length > 0) {
            showToast("Please fill empty column or delete it..!", false);
            return false;
        }

        const linkedKeyInTrackChange = getRefValueByPath(stateRef, "trackChanges").filter((col) => (!!col?.linkedKey))

        if ((wrapperSupport === "on") && syncing?.submits) {
            return {
                linkedKeyInTrackChange,
                columns: getRefValueByPath(stateRef, "columns"),
                trackChanges : getRefValueByPath(stateRef, "trackChanges"),
                tableType : config?.type
            }
        }

        if ((linkedKeyInTrackChange?.length > 0)) {
            setRefValueByPath(stateRef, "modelOpen", true)
            render();
        } else {
            finalSubmmit()
        }
    };

    !!config && (config.trackChanges = getRefValueByPath(stateRef, "trackChanges"))
    !!config && (config.SUBMIT_HANDLER = handleSave)

    return (
        <>
            <DialogBox
                state={getRefValueByPath(stateRef, "modelOpen")}
                hanldeToggle={() => {
                    setRefValueByPath(
                        stateRef,
                        "modelOpen",
                        !getRefValueByPath(stateRef, "modelOpen")
                    );
                    render();
                }}
                hanldeNo={() => {
                    setRefValueByPath(stateRef, "modelOpen", false);
                    render();
                }}
                handleYes={() => {
                    finalSubmmit()
                }}
                data={linkingData}
            />

        {!(isDynamicColumnType && previewHeaders?.length > 0) ? (
            <TableContainer
                sx={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#0000FF #00000000",
                }}
            >
                <Table aria-label="structure table">
                    <TableHead>
              {!config?.isViewMode ? (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="columns" direction="horizontal">
                                {(provided) => (
                                    <TableRow
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {stateRef.current.columns.map(
                                            (column, colIndex) => (
                                                <Draggable
                                                    key={column.id ?? colIndex}
                                                    draggableId={String(
                                                        column.id ?? colIndex
                                                    )}
                                                    index={colIndex}
                                                >
                                                    {(provided, snapshot) => (
                                                        <TableCell
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            sx={{
                                                                textAlign: "center",
                                                                minWidth: "300px",
                                                                background: snapshot.isDragging
                                                                    ? "rgba(0,0,0,0.05)"
                                                                    : "inherit",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    gap: "20px",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                <DragIndicatorIcon fontSize="small" />
                                                                <Typography variant="h6">
                                                                    {colIndex + 1}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        gap: "20px",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <StructureCell
                                                                        column={column}
                                                                        onChange={(newValue) =>
                                                                            handleChangeColumnName(
                                                                                column,
                                                                                colIndex,
                                                                                newValue
                                                                            )
                                                                        }
                                                                        config={config}
                                                                    />
                                                      {!!config?.onDelete?.isVisible &&
                                                        !config?.isViewMode && (
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() =>
                                                                            handleDelete(colIndex)
                                                                        }
                                                                        disabled={!config?.onDelete?.isActive}
                                                                    >
                                                                        <Delete />
                                                                    </IconButton>
                                                      )}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                </Draggable>
                                            )
                                        )}
                                        {provided.placeholder}
                                    </TableRow>
                                )}
                            </Droppable>
                        </DragDropContext>
              ) : (
                <TableRow>
                  {stateRef.current.columns.map((column, colIndex) => (
                    <TableCell
                      key={colIndex}
                      sx={{
                        textAlign: "center",
                        minWidth: "220px",
                        bgcolor: "#fafafa",
                        border: "1px solid #e0e0e0",
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        color: "#202124", // Google gray
                        letterSpacing: "0.2px",
                        boxShadow: "inset 0 -1px 0 #dadce0", // subtle divider line
                        borderRadius:
                          colIndex === 0
                            ? "12px 0 0 12px"
                            : colIndex === stateRef.current.columns.length - 1
                            ? "0 12px 12px 0"
                            : 0,
                        transition: "background 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#f1f3f4",
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.95rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column?.name}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ mt: 4 }}>
          <TableContainer>
            <Table>
              <TableHead>
                {previewHeaders?.map((level, rowIdx) => (
                  <TableRow key={rowIdx}>
                    {level.map((cell, cellIdx) => (
                      <TableCell
                        key={cellIdx}
                        colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                        rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "#f5f5f5",
                          textAlign: "center",
                        }}
                      >
                        {cell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
            </Table>
          </TableContainer>
        </Box>
      )}

      {!config?.isViewMode && (
            <Box
                sx={{
                    mt: "30px",
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
          {!!config?.addRow?.isVisible && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAdd}
                    disabled={!!!config?.addRow?.isActive || isDynamicColumnType}
                >
                    <Add />
                    <Typography>Add</Typography>
                </Button>
          )}

                {isSubmitVisible && !((wrapperSupport === "on") && syncing?.submits) && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleSave}
                        disabled={isDynamicColumnType || !isSubmitActive}
                    >
                        <Typography>Save</Typography>
                    </Button>
                )}
            </Box>
          )}
        </>
    );
};

const WrappedStructureTable = forwardRef(StructureTable);
WrappedStructureTable.__IS_DYNAMIC_TABLE__ = true;

export default WrappedStructureTable;
